import { Component } from "react"

/* Redux */
import { connect } from "react-redux"

/* Components */
import { InnerLayout, Pagination } from "../../components"

/* Widgets */
import { CurrencyForm } from "../../widgets/Currency"

/* Translate */
import { t } from "../../translates/translate"

/* REST API */
import { list, remove } from "../../api/Currency"

/* Helpers */
import { withRouter, utils } from "../../helpers"

/* Icons */
import { PlusIcon, EditIcon, TrashIcon, SearchIcon, CloseIcon, LockIcon, ChipIcon, TableIcon, CoinIcon } from "../../widgets/Currency/CurrencyIcons"

/* Sweet alert */
import Swal from "../../helpers/alert"


/* DELAY BEFORE THE SEARCH REQUEST LEAVES */
const DELAY = 400


/* Page Currencies */
class Currencies extends Component {

    constructor() {
        super()

        this.state = {
            data: [],
            status: "loading",
            count: 0,
            search: "",
            canEdit: false,
        }
    }

    componentDidMount = () => {
        window.scrollTo(0, 0)
        this.load()
    }

    componentDidUpdate = (prevProps) => {

        /* Fields */
        const { params } = this.props
        const { page } = params

        /* Check Page — a new page starts from the top with the loader */
        if (prevProps.params.page !== page) {
            window.scrollTo(0, 0)
            this.load(true)
        }

    }

    componentWillUnmount = () => {
        clearTimeout(this._timer)
    }

    /* Get Page */
    getPage = () => {

        /* Fields */
        const { params } = this.props
        const { page } = params

        /* Check Page */
        if (Number.isInteger(parseInt(page))) {
            return parseInt(page)
        }

        return 1
    }

    /* Load Action */
    load = (reload = false) => {

        /* Fields */
        const { search } = this.state
        const page = this.getPage()

        /* Check Reload */
        if (reload) {
            this.setState({ status: "loading" })
        }

        /* Send to REST API */
        list(page, search).then((response) => {
            if (response.status === 200) {
                this.setState({ status: "ok", data: response.data.list, count: response.data.count, canEdit: utils.checkBoolean(response.data.canEdit) })
            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ status: "permission", data: [], count: 0 })
            }
            else if (response.status === 498) {
                this.setState({ status: "key", data: [], count: 0 })
            }
            else {
                this.setState({ status: "error", data: [], count: 0 })
            }
        }).catch(() => {
            this.setState({ status: "network", data: [], count: 0 })
        })

    }

    /* Change Search — the request leaves only after the typing stops */
    changeSearch = (search) => {

        this.setState({ search })

        clearTimeout(this._timer)
        this._timer = setTimeout(() => this.search(), DELAY)
    }

    /* Search Action — every search restarts from the first page */
    search = () => {

        /* Fields */
        const { navigate } = this.props

        /* Check Page */
        if (this.getPage() !== 1) {
            navigate("/currency/1")
            return
        }

        this.load()
    }

    /* Remove Action */
    remove = (item) => {

        Swal.fire({
            title: t("swal.deleteConfirm"),
            text: `${item.name} (${item.code})`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#f44336",
            reverseButtons: true,
            confirmButtonText: t("swal.deleteButton"),
            cancelButtonText: t("swal.cancelButton"),
        }).then((result) => {
            if (result.isConfirmed) {
                remove({ id: item.id }).then((response) => {
                    if (response.status === 200) {
                        this.load(true)
                        Swal.fire(utils.notification(t("currency.deletedSuccess"), "success"))
                    }
                    else if (response.data && response.data.message) {
                        Swal.fire(utils.notification(response.data.message, "error"))
                    }
                    else if (response.status === 498) {
                        Swal.fire(utils.notification(t("swal.invalidKey"), "error"))
                    }
                    else if (response.status === 401 || response.status === 403) {
                        Swal.fire(utils.notification(t("swal.noAccess"), "error"))
                    }
                    else {
                        Swal.fire(utils.notification(t("swal.generic"), "error"))
                    }
                }).catch(() => {
                    Swal.fire(utils.notification(t("swal.generic"), "error"))
                })
            }
        })
    }

    /* Draw Search */
    _search = () => {

        /* Fields */
        const { search } = this.state

        return (
            <div className="currency--search">

                <SearchIcon />

                <input
                    value={search}
                    onChange={(event) => this.changeSearch(event.target.value)}
                    placeholder={t("currency.searchByName")}
                />

                {search !== "" &&
                    <div onClick={() => this.changeSearch("")} className="currency--search-clear">
                        <CloseIcon size={12} />
                    </div>
                }

            </div>
        )
    }

    /* Draw Symbol Preview — shows the exact order the panel and the game display it in */
    _preview = (item) => (
        <div className="currency--preview">
            {parseInt(item.isAfter) === 1 ? `1 000 ${item.symbol}` : `${item.symbol} 1 000`}
        </div>
    )

    /* Draw Chip Rate */
    _chipRate = (item) => (
        <div className="currency--chip-rate">
            <ChipIcon />
            <span>× {utils.checkValue(item.chipRate, "1")}</span>
        </div>
    )

    /* Draw Actions */
    _actions = (item) => {

        /* Fields */
        const { canEdit } = this.state

        /* Not A Superadmin — every change is closed */
        if (!canEdit) {
            return (
                <div className="currency--action-locked">
                    <LockIcon />
                    <span>{t("currency.superadminOnly")}</span>
                </div>
            )
        }

        return (
            <>
                <div onClick={() => this._form.open({ type: "edit", id: item.id })} className="currency--action" title={t("currency.edit")}>
                    <EditIcon />
                </div>

                <div onClick={() => this.remove(item)} className="currency--action red" title={t("currency.delete")}>
                    <TrashIcon />
                </div>
            </>
        )
    }

    /* Draw Content */
    _content = () => {

        /* Fields */
        const { status, data, count, search } = this.state
        const { navigate } = this.props
        const page = this.getPage()

        /* Check Status */
        if (status !== "ok") {
            return (
                <div className="currency--page">
                    <InnerLayout inner status={status} reload={() => this.load(true)} />
                </div>
            )
        }

        return (
            <div className="currency--page">

                {/* Head */}
                <div className="currency--table-header">
                    <div className="currency--column number">#</div>
                    <div className="currency--column name">{t("currency.name")}</div>
                    <div className="currency--column code">{t("currency.code")}</div>
                    <div className="currency--column rate">{t("currency.chipRateShort")}</div>
                    <div className="currency--column tables">{t("currency.usedOn")}</div>
                    <div className="currency--column actions" />
                </div>

                {/* Empty */}
                {data.length === 0 &&
                    <div className="currency--empty">
                        <CoinIcon size={40} />
                        <span>{search !== "" ? t("currency.nothingFound") : t("currency.empty")}</span>
                    </div>
                }

                {/* Body */}
                {data.map((item, index) =>
                    <div className="currency--row" key={`${index}`}>

                        <div className="currency--column number">{(page - 1) * 10 + index + 1}</div>

                        <div className="currency--column name">

                            <div className="currency--avatar">{item.symbol}</div>

                            <div className="currency--person-text">
                                <div className="currency--person-name">{item.name}</div>
                                {this._preview(item)}
                            </div>

                        </div>

                        <div className="currency--column code">
                            <div className="currency--code">{item.code}</div>
                        </div>

                        <div className="currency--column rate">{this._chipRate(item)}</div>

                        <div className="currency--column tables">
                            <div className={`currency--tables ${parseInt(item.tablecount) === 0 ? "empty" : ""}`}>
                                <TableIcon />
                                <span>{item.tablecount}</span>
                            </div>
                        </div>

                        <div className="currency--column actions">{this._actions(item)}</div>

                    </div>
                )}

                {/* Pagination */}
                <Pagination size={10} page={page} total={count} onPageChange={(page) => navigate(`/currency/${page}`)} />

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { status, canEdit } = this.state

        return (
            <div className="page">

                <div className="page-header currency--header">

                    <h1>{t("currency.title")}</h1>

                    {status === "ok" &&
                        <div className="currency--tools">

                            {canEdit &&
                                <div onClick={() => this._form.open({ type: "create" })} className="button currency--add">
                                    <PlusIcon size={14} />
                                    <span>{t("common.add")}</span>
                                </div>
                            }

                            {/* Draw Search */}
                            {this._search()}

                        </div>
                    }

                </div>

                {/* Draw Content */}
                {this._content()}

                {/* Draw Form Modal */}
                <CurrencyForm ref={(ref) => this._form = ref} reload={() => this.load()} />

            </div>
        )
    }

}

const mapStateToProps = (state) => ({
    admin: state.admin,
    language: state.language,
})

export default connect(mapStateToProps)(withRouter(Currencies))
